import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { httpClient } from "../../../services/httpClient";
import { showToast } from "../../../utils/showToast";

const CLAVE_TOKEN = "token_expiracion_horas";
const CLAVE_MAX_FALTAS = "mencion_honor_max_faltas";
const CLAVE_EXCLUYE_DISTANCIA = "mencion_honor_excluye_distancia";

export default function ConfiguracionSistema() {
  const [horas, setHoras]   = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [maxFaltas, setMaxFaltas] = useState("");
  const [excluyeDistancia, setExcluyeDistancia] = useState(true);
  const [savingMencion, setSavingMencion] = useState(false);

  useEffect(() => {
    httpClient.get("/api/configuracion")
      .then((data) => {
        const map = Object.fromEntries(data.map((r) => [r.clave, r.valor]));
        setHoras(map[CLAVE_TOKEN] ?? "8");
        setMaxFaltas(map[CLAVE_MAX_FALTAS] ?? "3");
        setExcluyeDistancia((map[CLAVE_EXCLUYE_DISTANCIA] ?? "1") === "1");
      })
      .catch(() => showToast({ type: "danger", message: "No se pudo cargar la configuración." }))
      .finally(() => setLoading(false));
  }, []);

  const guardar = async () => {
    const val = parseInt(horas, 10);
    if (isNaN(val) || val < 1 || val > 720) {
      showToast({ type: "warning", message: "Ingresa un valor entre 1 y 720 horas." });
      return;
    }
    setSaving(true);
    try {
      await httpClient.put(`/api/configuracion/${CLAVE_TOKEN}`, { valor: String(val) });
      showToast({ type: "success", message: "Configuración guardada. Aplica en el próximo inicio de sesión." });
    } catch {
      showToast({ type: "danger", message: "No se pudo guardar." });
    } finally {
      setSaving(false);
    }
  };

  const guardarMencion = async () => {
    const val = parseInt(maxFaltas, 10);
    if (isNaN(val) || val < 0 || val > 999) {
      showToast({ type: "warning", message: "Ingresa un número de faltas entre 0 y 999." });
      return;
    }
    setSavingMencion(true);
    try {
      await httpClient.put(`/api/configuracion/${CLAVE_MAX_FALTAS}`, { valor: String(val) });
      await httpClient.put(`/api/configuracion/${CLAVE_EXCLUYE_DISTANCIA}`, { valor: excluyeDistancia ? "1" : "0" });
      showToast({ type: "success", message: "Configuración de mención de honor guardada." });
    } catch {
      showToast({ type: "danger", message: "No se pudo guardar." });
    } finally {
      setSavingMencion(false);
    }
  };

  return (
    <section className="management-page management-theme-teal configuracion-wrapper">
      <div className="container-fluid">
        <div className="management-card management-header mb-4">
          <div className="d-flex align-items-center gap-2 mb-1">
            <Link to="/dashboard/configuracion" className="text-muted text-decoration-none small">
              <i className="bi bi-chevron-left me-1"></i>Configuración
            </Link>
          </div>
          <div className="dashboard-eyebrow">Configuración / Sistema</div>
          <h5 className="mb-1">Parámetros del sistema</h5>
          <p className="text-muted mb-0">Ajustes generales de seguridad y comportamiento de la plataforma.</p>
        </div>

        {loading ? (
          <div className="text-muted">Cargando...</div>
        ) : (
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="config-card-icon">
                      <i className="bi bi-shield-lock"></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-semibold">Duración de sesión</h6>
                      <small className="text-muted">Horas que dura el token de acceso</small>
                    </div>
                  </div>
                  <p className="text-muted small mb-3">
                    Tiempo en horas que un usuario permanece conectado sin necesidad de volver a iniciar sesión.
                    El cambio aplica únicamente a los nuevos inicios de sesión.
                  </p>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      max="720"
                      step="1"
                      value={horas}
                      onChange={(e) => setHoras(e.target.value)}
                    />
                    <span className="input-group-text">horas</span>
                    <button
                      className="btn management-btn-primary"
                      disabled={saving}
                      onClick={guardar}
                    >
                      {saving ? "Guardando..." : "Guardar"}
                    </button>
                  </div>
                  <small className="text-muted mt-2 d-block">
                    Ejemplos: 8 h (jornada laboral) · 24 h (un día) · 168 h (una semana)
                  </small>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="config-card-icon">
                      <i className="bi bi-award"></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-semibold">Mención de honor</h6>
                      <small className="text-muted">Requisitos de asistencia</small>
                    </div>
                  </div>
                  <p className="text-muted small mb-2">
                    Máximo de faltas (ausente) que puede tener un estudiante aprobado para seguir
                    recibiendo mención de honor.
                  </p>
                  <div className="input-group mb-3">
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      max="999"
                      step="1"
                      value={maxFaltas}
                      onChange={(e) => setMaxFaltas(e.target.value)}
                    />
                    <span className="input-group-text">faltas máx.</span>
                  </div>
                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="excluye-distancia"
                      checked={excluyeDistancia}
                      onChange={(e) => setExcluyeDistancia(e.target.checked)}
                    />
                    <label className="form-check-label small" htmlFor="excluye-distancia">
                      Excluir de la mención a estudiantes con matrícula a distancia
                    </label>
                  </div>
                  <button
                    className="btn management-btn-primary"
                    disabled={savingMencion}
                    onClick={guardarMencion}
                  >
                    {savingMencion ? "Guardando..." : "Guardar"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
